import { nanoid } from 'nanoid';
import type { Sheet } from './store';
import type { ProjectMeta } from './types';

const KEY = 'skema:autosave';

export interface AutosaveBlob {
  version: 2;
  savedAt: string;
  meta: ProjectMeta;
  sheets: Sheet[];
  activeSheetId: string;
}

export function writeAutosave(
  meta: ProjectMeta,
  sheets: Sheet[],
  activeSheetId: string,
): void {
  const blob: AutosaveBlob = {
    version: 2,
    savedAt: new Date().toISOString(),
    meta,
    sheets,
    activeSheetId,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(blob));
  } catch {
    // quota exceeded or similar — silently ignore
  }
}

/** Accept both v1 (flat nodes/edges) and v2 (sheets) formats. */
export function readAutosave(): AutosaveBlob | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.meta) return null;

    if (parsed.version === 2 && Array.isArray(parsed.sheets)) {
      return parsed as AutosaveBlob;
    }

    // v1 migration: wrap flat nodes/edges into a single schematic sheet.
    if ((parsed.version === 1 || parsed.version === undefined) && parsed.nodes && parsed.edges) {
      const id = nanoid(8);
      return {
        version: 2,
        savedAt: parsed.savedAt ?? new Date().toISOString(),
        meta: parsed.meta,
        sheets: [
          { id: nanoid(8), name: 'Block Diagram', kind: 'block', nodes: [], edges: [] },
          { id, name: 'Schematic', kind: 'schematic', nodes: parsed.nodes, edges: parsed.edges },
        ],
        activeSheetId: id,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearAutosave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
